import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';

/**
 * Servicio para gestionar la subida de archivos a Firebase Storage
 */
export const storageService = {
  /**
   * Sube un comprobante de pago a Firebase Storage
   * @param {File} file - Archivo a subir
   * @param {string} userId - ID del usuario
   * @param {string} reservationId - ID de la reserva
   * @returns {Promise<string>} URL de descarga del archivo
   */
  uploadPaymentReceipt: async (file, userId, reservationId) => {
    try {
      // Validar el archivo
      if (!file) {
        throw new Error('No se proporcionó un archivo');
      }

      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Tipo de archivo no válido. Solo se permiten JPG, PNG o PDF');
      }

      // Validar tamaño (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('El archivo no debe superar los 5MB');
      }

      // Generar nombre único para el archivo
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `comprobante_${timestamp}.${fileExtension}`;

      // Crear la ruta en Storage: comprobantes/{userId}/{reservationId}/{fileName}
      const storagePath = `comprobantes/${userId}/${reservationId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // Metadata del archivo
      const metadata = {
        contentType: file.type,
        customMetadata: {
          userId: userId,
          reservationId: reservationId,
          uploadDate: new Date().toISOString(),
        },
      };

      // Subir el archivo
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log('Archivo subido exitosamente:', snapshot.metadata.fullPath);

      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        path: storagePath,
        fileName: fileName,
      };
    } catch (error) {
      console.error('Error al subir el comprobante:', error);
      throw new Error(`Error al subir el comprobante: ${error.message}`);
    }
  },

  /**
   * Elimina un comprobante de pago de Firebase Storage
   * @param {string} filePath - Ruta del archivo en Storage
   * @returns {Promise<void>}
   */
  deletePaymentReceipt: async (filePath) => {
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
      console.log('Archivo eliminado exitosamente:', filePath);
    } catch (error) {
      console.error('Error al eliminar el comprobante:', error);
      throw new Error(`Error al eliminar el comprobante: ${error.message}`);
    }
  },

  /**
   * Obtiene la URL de descarga de un comprobante
   * @param {string} filePath - Ruta del archivo en Storage
   * @returns {Promise<string>} URL de descarga
   */
  getReceiptDownloadURL: async (filePath) => {
    try {
      const fileRef = ref(storage, filePath);
      const downloadURL = await getDownloadURL(fileRef);
      return downloadURL;
    } catch (error) {
      console.error('Error al obtener la URL del comprobante:', error);
      throw new Error(`Error al obtener la URL del comprobante: ${error.message}`);
    }
  },

  /**
   * Valida si un archivo es válido para subir
   * @param {File} file - Archivo a validar
   * @returns {Object} { valid: boolean, error: string|null }
   */
  validateFile: (file) => {
    if (!file) {
      return { valid: false, error: 'No se proporcionó un archivo' };
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de archivo no válido. Solo se permiten JPG, PNG o PDF'
      };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'El archivo no debe superar los 5MB'
      };
    }

    return { valid: true, error: null };
  },
};

export default storageService;
